import { useState } from 'react'
import type { TmsInvoiceAgingBucket, TmsInvoiceRow } from '@numeo/types-main-web-app'
import type { ListPage, ThreadLoad } from './cardResults'
import { CardPageFooter } from './CardPageFooter'
import { InvoiceRow } from './InvoiceRow'
import { agingSectionsOf } from './invoiceRows'
import { InvoicingRowsTable } from '../invoicing/InvoicingTable'
import { isReadyTabRow } from './StatusBadge'

interface InvoicesTableProps {
    rows: TmsInvoiceRow[]
    /** The card whose answer this is: its next page is read into it. Without one, the footer offers no next page. */
    cardId?: string
    /** How much of the list the rows are, when the card said. */
    page?: ListPage
    /** Loads the thread knows by id, for a row from before the card carried its own `load_id`. */
    loads: ThreadLoad[]
    /** The user's message the answer is for: a ready-list question opens the table on Ready. */
    question?: string
    onSend: (message: string) => void
}

const rowKey = (row: TmsInvoiceRow, i: number) => row.invoice_id ?? `${row.load_numbers.join(',')}-${i}`

/** The rows an aged answer draws before its one control reveals the rest of the rows the answer already holds. */
const AGED_ROWS_FIRST = 10

/** An aged answer: its rows in sections by days outstanding, each headed by its count and amount over the whole aged set. */
const AgingBuckets = ({
    rows,
    buckets,
    thresholdDays,
    onSend,
}: {
    rows: TmsInvoiceRow[]
    buckets?: TmsInvoiceAgingBucket[]
    thresholdDays?: number
    onSend: (message: string) => void
}) => (
    <div className="flex flex-col gap-4">
        {agingSectionsOf(rows, buckets).map((section) => (
            <section key={section.label} aria-label={`${section.label}: ${section.count} invoices`} className="flex flex-col gap-1">
                <header className="flex items-baseline justify-between gap-2 text-xs text-foreground-muted">
                    <span className="font-semibold uppercase tracking-wider">{section.label}</span>
                    <span className="tabular-nums">
                        {section.totalLabel
                            ? `${section.count.toLocaleString('en-US')} · ${section.totalLabel}`
                            : `${section.count.toLocaleString('en-US')} shown`}
                    </span>
                </header>
                <ul className="flex flex-col">
                    {section.rows.map((row, i) => (
                        <InvoiceRow key={rowKey(row, i)} row={row} thresholdDays={thresholdDays} onSend={onSend} />
                    ))}
                </ul>
            </section>
        ))}
    </div>
)

/**
 * A `tms_invoices.list` answer (plan FR-G3). Ready-tab loads are the v17 in-chat invoicing table (its filters, selection, Review and
 * drawers), the same as every invoicing answer. Invoice rows are one list on the load line's grid; an aged answer groups them into sections instead.
 * One control at the bottom: it reveals the rows the answer already holds, then reads the next server page.
 */
export const InvoicesTable = ({ rows, cardId, page, question, onSend }: InvoicesTableProps) => {
    const [revealed, setRevealed] = useState(false)
    const aged = page?.query?.tab === 'aged'
    const ready = rows.some(isReadyTabRow)
    const capped = aged && !ready && !revealed
    const drawn = capped ? rows.slice(0, AGED_ROWS_FIRST) : rows
    const thresholdDays = page?.aging?.[0]?.min_days
    return (
        <div className="flex flex-col gap-3">
            {ready ? (
                // Ready-tab loads are invoicing rows: the same v17 table as every invoicing answer (decision 10).
                <InvoicingRowsTable messageId={cardId ?? 'invoices'} rows={rows} total={page?.total} question={question} />
            ) : aged ? (
                <AgingBuckets rows={drawn} buckets={page?.aging} thresholdDays={thresholdDays} onSend={onSend} />
            ) : (
                <ul className="flex flex-col" aria-label="Invoices">
                    {rows.map((row, i) => (
                        <InvoiceRow key={rowKey(row, i)} row={row} onSend={onSend} />
                    ))}
                </ul>
            )}
            <CardPageFooter cardId={cardId} page={page} subject="invoices" localHidden={rows.length - drawn.length} onRevealLocal={() => setRevealed(true)} />
        </div>
    )
}
